//helpers
import { getUrl, ROUTES } from 'shared/api/urls';
import { ApiHelper } from 'shared/helpers/api';
import { sendActionOTPAccountDelete, executeUserActionAccountDelete } from 'shared/api/authJwt';

export interface OTPData {
  otp_id: string;
  otp?: string;
  password?: string;
}

const DOMAIN = 'fashion';
const SOURCE = 'react';

const sendResponse = <T extends object>(
  res: any,
  extras: T,
  overrideMessage?: string
): { status: string; message: string } & T => ({
  status: res?.status || 'fail',
  message: overrideMessage ?? res?.message,
  ...extras,
});

export const checkUserAccountDeleteEligibility = (): Promise<any> => {
  const url = getUrl(ROUTES.CHECK_ACCOUNT_DELETE_ELIGIBILITY);
  return ApiHelper(`${url}?domain=${DOMAIN}`, {
    method: 'GET',
    isBodyJSON: true,
  })
    .then((data: Response) => {
      if (!data.ok) {
        return Promise.reject({ statusCode: data?.status, data });
      }
      return data.json();
    })
    .catch(() => Promise.reject({ statusCode: 500 }));
};

export const deleteUserAccount = (otpData: OTPData) => {
  const baseUrl = getUrl(ROUTES.ACCOUNT_DELETE);
  const { otp, otp_id } = otpData;
  const body = { domain: DOMAIN, otp_id, otp, source: SOURCE };

  return ApiHelper(`${baseUrl}`, { method: 'POST', body, isBodyJSON: true })
    .then((response: Response) => {
      if (!response.ok) {
        return Promise.reject({ statusCode: response?.status, data: response });
      }
      return response.json();
    })
    .catch(() => Promise.reject({ statusCode: 500 }));
};

/**
 * Grahak adapter for deleteUserAccount. Calls
 * /grahak-service/api/v1/user/actions/execute with `action: DELETE_ACCOUNT`,
 * passing `otp_id` (received from sendActionOtp) as `authId` and the user's OTP.
 * Projects the response onto the legacy `{ status, message }` contract.
 */
export const deleteUserAccountGrahak = async (otpData: OTPData): Promise<any> => {
  const { otp = '', otp_id: authId } = otpData;
  try {
    const res = await executeUserActionAccountDelete(authId, otp);
    return sendResponse(res, { response: res?.response });
  } catch (e: any) {
    return Promise.reject({ statusCode: e?.statusCode || 500 });
  }
};

export const sendAccountDeleteOtp = ({ userMobile }: { userMobile: string }) => {
  const baseUrl = getUrl(ROUTES.ACCOUNT_DELETE_OTP);
  const body = { domain: DOMAIN, mobile: userMobile, source: SOURCE };

  return ApiHelper(`${baseUrl}`, { method: 'POST', body, isBodyJSON: true })
    .then((response: Response) => {
      if (!response.ok) {
        return Promise.reject({ statusCode: response?.status, data: response });
      }
      return response.json();
    })
    .catch(() => Promise.reject({ statusCode: 500 }));
};

/**
 * Grahak adapter for sendAccountDeleteOtp. Calls /grahak-service/api/v1/otp/sendActionOtp
 * (action = DELETE_ACCOUNT) via the existing JWT helper and projects the response
 * onto the legacy contract so the consumer (`OTPVerification.tsx`) stays agnostic.
 * `userMobile` is intentionally unused — Grahak derives identity from the JWT.
 */
export const sendAccountDeleteOtpGrahak = async (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _args: { userMobile: string }
): Promise<any> => {
  try {
    const res = await sendActionOTPAccountDelete();
    const r = res?.response || {};
    return sendResponse(
      res,
      {
        data: {
          otpId: r.otpId || '',
          retryAllowed: r.retryAllowed ?? 3,
          resendAllowed: r.resendAllowed ?? 3,
        },
      },
      r.message || res?.message
    );
  } catch (e: any) {
    return Promise.reject({ statusCode: e?.statusCode || 500 });
  }
};

export const resendAccountDeleteOtp = (otpData: OTPData) => {
  const baseUrl = getUrl(ROUTES.RESEND_ACCOUNT_DELETE_OTP);
  const body = { domain: DOMAIN, otp_id: otpData.otp_id, source: SOURCE };

  return ApiHelper(`${baseUrl}`, { method: 'POST', body, isBodyJSON: true })
    .then((response: Response) => {
      if (!response.ok) {
        return Promise.reject({ statusCode: response?.status, data: response });
      }
      return response.json();
    })
    .catch(() => Promise.reject({ statusCode: 500 }));
};

/**
 * Grahak adapter for resendAccountDeleteOtp. There's no dedicated resend
 * endpoint for action OTPs — we just re-call sendActionOtp with
 * action=DELETE_ACCOUNT, which mints a fresh authId. The existing `otp_id`
 * argument is intentionally unused.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const resendAccountDeleteOtpGrahak = (_otpData: OTPData): Promise<any> =>
  sendAccountDeleteOtpGrahak({ userMobile: '' });
